import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getContent, adminCreate, adminUpdate, adminDelete, asset } from '@/lib/api';
import { CRUD_CONFIGS } from '@/pages/admin/crudConfigs';
import { AdminField } from '@/pages/admin/AdminField';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function CrudManager() {
  const { collection } = useParams();
  const config = CRUD_CONFIGS[collection];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => getContent(collection).then(setItems).catch(() => {});
  useEffect(() => { if (config) load(); /* eslint-disable-next-line */ }, [collection]);

  if (!config) return <div className='p-8 text-[#20272A]/60'>Unknown collection.</div>;

  const openNew = () => { setEditing(null); setForm({ order: items.length + 1 }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };
  const setField = (name, val) => setForm((f) => ({ ...f, [name]: val }));

  const save = async () => {
    setLoading(true);
    try {
      if (editing) { await adminUpdate(collection, editing.id, form); toast.success('Updated'); }
      else { await adminCreate(collection, form); toast.success('Created'); }
      setOpen(false); load();
    } catch (e) { toast.error('Save failed'); }
    finally { setLoading(false); }
  };

  const doDelete = async () => {
    try { await adminDelete(collection, confirmDel.id); toast.success('Deleted'); setConfirmDel(null); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='font-head text-2xl font-semibold text-[#20272A]'>{config.label}</h1>
          <p className='text-sm text-[#20272A]/50'>{items.length} item(s)</p>
        </div>
        <button onClick={openNew} className='inline-flex h-10 items-center gap-2 rounded-lg bg-[#075B61] px-4 text-sm font-medium text-white hover:bg-[#0a6d74]' data-testid='admin-add-button'>
          <Plus size={16} /> Add {config.label.replace(/s$/, '')}
        </button>
      </div>

      <div className='mt-6 overflow-hidden rounded-xl border border-[#20272A]/10 bg-white' data-testid='admin-crud-table'>
        <table className='w-full text-left text-sm'>
          <thead className='border-b border-[#20272A]/10 bg-[#F6F5F1] text-xs uppercase tracking-wider text-[#20272A]/50'>
            <tr>
              <th className='px-4 py-3'>Title</th>
              <th className='hidden px-4 py-3 sm:table-cell'>Detail</th>
              <th className='px-4 py-3 text-right'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className='border-b border-[#20272A]/6 last:border-0'>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    {(item.image || item.heroImage) && <img src={asset(item.image || item.heroImage)} alt='' className='h-9 w-12 rounded object-cover' />}
                    <span className='font-medium text-[#20272A]'>{item[config.titleField] || '—'}</span>
                  </div>
                </td>
                <td className='hidden px-4 py-3 text-[#20272A]/55 sm:table-cell'>{item[config.subtitleField] || '—'}</td>
                <td className='px-4 py-3'>
                  <div className='flex items-center justify-end gap-2'>
                    <button onClick={() => openEdit(item)} className='inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#20272A]/10 text-[#075B61] hover:bg-[#075B61]/5' data-testid='admin-edit-button'><Pencil size={14} /></button>
                    <button onClick={() => setConfirmDel(item)} className='inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#20272A]/10 text-red-600 hover:bg-red-50' data-testid='admin-delete-button'><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className='px-4 py-10 text-center text-[#20272A]/40'>No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-h-[88vh] max-w-3xl overflow-y-auto'>
          <DialogHeader><DialogTitle className='font-head'>{editing ? 'Edit' : 'Add'} {config.label.replace(/s$/, '')}</DialogTitle></DialogHeader>
          <div className='grid gap-4 sm:grid-cols-2'>
            {config.fields.map((f) => (
              <AdminField key={f.name} field={f} value={form[f.name]} onChange={(v) => setField(f.name, v)} />
            ))}
          </div>
          <div className='mt-4 flex justify-end gap-3'>
            <button onClick={() => setOpen(false)} className='rounded-lg border border-[#20272A]/15 px-4 py-2 text-sm text-[#20272A]/70'>Cancel</button>
            <button onClick={save} disabled={loading} className='rounded-lg bg-[#075B61] px-5 py-2 text-sm font-medium text-white hover:bg-[#0a6d74] disabled:opacity-60' data-testid='admin-save-button'>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className='bg-red-600 hover:bg-red-700'>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
